// client/src/components/messaging/ThreadAISummary.tsx
// AI-powered thread summary and reply suggestions component

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, AlertCircle, ChevronDown, ChevronUp, Zap, Clock, CheckCircle, XCircle } from 'lucide-react';
import {
  useGetThreadAssistantQuery,
  useGetAIMessagingStatusQuery,
} from '../../api/aiMessagingApi';
import { useCreateTaskMutation } from '../../api/tasksApi';
import { useHasCapability } from '../../services/centralContext';
import { FEATURE_CAPABILITIES } from '../../config/featureCapabilities';
import type { ReplySuggestion, SuggestedAction, ThreadSummary } from '../../types/api';

interface ThreadAISummaryProps {
  threadId: string;
  onSelectSuggestion?: (text: string) => void;
  /**
   * Active workspace. Suggested actions that create records need it; without
   * it no action is executable, so none is rendered.
   */
  workspaceId?: string;
}

/**
 * Suggested-action types that map onto an EXISTING frontend create path.
 *
 *   create_task  → tasksApi.createTask (POST /tasks), gated on tasks.write.
 *   create_event → the canonical prefilled create flow on /schedule
 *                  (`?new=1&contactId=…&conversationThreadId=…`), the same
 *                  deep link ContactWorkspaceDrawer uses. The assistant's
 *                  payload carries no start time and `createEvent` requires
 *                  one, so the user picks the time there; that form calls
 *                  eventsApi.createEvent. Gated on events.write.
 *
 * Every other type (add_contact, send_follow_up, add_memory, update_contact)
 * has no mapped client path and is NOT rendered — an "Execute" button that
 * does nothing is worse than no button.
 */
type ExecutableActionType = 'create_task' | 'create_event';

function payloadString(action: SuggestedAction, key: string): string | undefined {
  const v = action.payload?.[key];
  return typeof v === 'string' && v.trim() ? v.trim() : undefined;
}

type ActionNotice = { kind: 'success' | 'error'; message: string };

function errorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object' && 'data' in err) {
    const d = (err as { data?: { message?: string; error?: string } }).data;
    return d?.message || d?.error || fallback;
  }
  return fallback;
}

const urgencyColors = {
  low: 'bg-green-100 text-green-700',
  medium: 'bg-yellow-100 text-yellow-700',
  high: 'bg-red-100 text-red-700',
};

const sentimentIcons = {
  positive: <CheckCircle className="w-4 h-4 text-green-500" />,
  neutral: <Clock className="w-4 h-4 text-gray-400" />,
  negative: <XCircle className="w-4 h-4 text-red-500" />,
  mixed: <AlertCircle className="w-4 h-4 text-yellow-500" />,
};

export function ThreadAISummary({
  threadId,
  onSelectSuggestion,
  workspaceId,
}: ThreadAISummaryProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [showActions, setShowActions] = useState(false);
  const [pendingActionId, setPendingActionId] = useState<string | null>(null);
  const [notice, setNotice] = useState<ActionNotice | null>(null);
  const navigate = useNavigate();
  const [createTask] = useCreateTaskMutation();
  const canCreateTask = useHasCapability(
    FEATURE_CAPABILITIES.createTask.domain,
    FEATURE_CAPABILITIES.createTask.action
  );
  // Same capability the command palette's "Create Event" entry uses.
  const canCreateEvent = useHasCapability('events', 'write');

  // A result notice belongs to the thread it was produced on.
  useEffect(() => {
    setNotice(null);
  }, [threadId]);

  const isExecutable = (action: SuggestedAction): boolean => {
    if (!workspaceId) return false;
    switch (action.type as ExecutableActionType | string) {
      case 'create_task':
        return canCreateTask && !!payloadString(action, 'title');
      case 'create_event':
        return canCreateEvent;
      default:
        return false;
    }
  };

  const executeAction = async (action: SuggestedAction) => {
    if (!workspaceId || !isExecutable(action) || pendingActionId) return;
    setNotice(null);
    if (action.type === 'create_event') {
      const params = new URLSearchParams({ new: '1', kind: 'meeting' });
      const contactId = payloadString(action, 'contactId');
      if (contactId) params.set('contactId', contactId);
      params.set('conversationThreadId', payloadString(action, 'threadId') ?? threadId);
      navigate(`/schedule?${params.toString()}`);
      return;
    }
    // create_task
    setPendingActionId(action.id);
    try {
      const title = payloadString(action, 'title') as string;
      await createTask({
        workspaceId,
        title,
        description: action.explanation || undefined,
        relatedContactId: payloadString(action, 'contactId'),
      }).unwrap();
      setNotice({ kind: 'success', message: `Task created: ${title}` });
    } catch (err) {
      setNotice({ kind: 'error', message: errorMessage(err, 'Failed to create task') });
    } finally {
      setPendingActionId(null);
    }
  };

  // Check if AI messaging is available
  const { data: statusData } = useGetAIMessagingStatusQuery();

  // Fetch AI assistant data
  const {
    data: assistantData,
    isLoading,
    error,
    refetch,
  } = useGetThreadAssistantQuery(threadId, {
    skip: !statusData?.available,
  });

  // Don't render if AI messaging is not available
  if (!statusData?.available) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="px-6 py-3 bg-gradient-to-r from-purple-50 to-indigo-50 border-b border-purple-100">
        <div className="flex items-center gap-2 text-purple-600">
          <Sparkles className="w-4 h-4 animate-pulse" />
          <span className="text-sm">Analyzing conversation...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-6 py-3 bg-red-50 border-b border-red-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-red-600">
            <AlertCircle className="w-4 h-4" />
            <span className="text-sm">Failed to load AI insights</span>
          </div>
          <button
            onClick={() => refetch()}
            className="text-xs text-red-600 hover:text-red-800 underline"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!assistantData) {
    return null;
  }

  const { summary, suggestions } = assistantData;
  const actions = (assistantData.actions ?? []).filter(isExecutable);

  return (
    <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border-b border-purple-100">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-6 py-3 flex items-center justify-between hover:bg-purple-50/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-purple-600" />
          <span className="text-sm font-medium text-purple-900">AI Insights</span>
          {summary && (
            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${urgencyColors[summary.urgency]}`}>
              {summary.urgency} priority
            </span>
          )}
        </div>
        {isExpanded ? (
          <ChevronUp className="w-4 h-4 text-purple-500" />
        ) : (
          <ChevronDown className="w-4 h-4 text-purple-500" />
        )}
      </button>

      {isExpanded && (
        <div className="px-6 pb-4 space-y-4">
          {/* Summary */}
          {summary && (
            <SummarySection summary={summary} />
          )}

          {/* Reply Suggestions */}
          {suggestions.length > 0 && (
            <SuggestionsSection
              suggestions={suggestions}
              onSelect={onSelectSuggestion}
            />
          )}

          {/* Suggested Actions — only the executable ones (see above). */}
          {actions.length > 0 && (
            <ActionsSection
              actions={actions}
              isExpanded={showActions}
              onToggle={() => setShowActions(!showActions)}
              onExecute={executeAction}
              pendingActionId={pendingActionId}
            />
          )}

          {notice && (
            <p
              role={notice.kind === 'error' ? 'alert' : 'status'}
              data-testid="ai-action-notice"
              className={`text-xs ${notice.kind === 'error' ? 'text-red-600' : 'text-green-700'}`}
            >
              {notice.message}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function SummarySection({ summary }: { summary: ThreadSummary }) {
  return (
    <div className="bg-white/60 rounded-lg p-3 space-y-2">
      <div className="flex items-start gap-2">
        {sentimentIcons[summary.sentiment]}
        <p className="text-sm text-gray-700 flex-1">{summary.summary}</p>
      </div>

      {summary.keyPoints.length > 0 && (
        <div className="pt-2 border-t border-purple-100">
          <p className="text-xs font-medium text-purple-700 mb-1">Key Points:</p>
          <ul className="space-y-1">
            {summary.keyPoints.map((point, idx) => (
              <li key={idx} className="text-xs text-gray-600 flex items-start gap-1.5">
                <span className="text-purple-400 mt-0.5">-</span>
                {point}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function SuggestionsSection({
  suggestions,
  onSelect,
}: {
  suggestions: ReplySuggestion[];
  onSelect?: (text: string) => void;
}) {
  return (
    <div>
      <p className="text-xs font-medium text-purple-700 mb-2">Quick Replies:</p>
      <div className="flex flex-wrap gap-2">
        {suggestions.map((suggestion) => (
          <button
            key={suggestion.id}
            onClick={() => onSelect?.(suggestion.text)}
            className="group px-3 py-1.5 text-sm bg-white border border-purple-200 rounded-full text-purple-700 hover:bg-purple-100 hover:border-purple-300 transition-colors flex items-center gap-1.5"
            title={`${suggestion.category} - ${suggestion.tone}`}
          >
            <Zap className="w-3 h-3 text-purple-400 group-hover:text-purple-600" />
            <span className="truncate max-w-[200px]">{suggestion.text}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function ActionsSection({
  actions,
  isExpanded,
  onToggle,
  onExecute,
  pendingActionId,
}: {
  actions: SuggestedAction[];
  isExpanded: boolean;
  onToggle: () => void;
  onExecute: (action: SuggestedAction) => void;
  pendingActionId: string | null;
}) {
  const actionTypeLabels: Record<string, string> = {
    create_task: 'Create Task',
    create_event: 'Schedule Meeting',
    add_contact: 'Add Contact',
    send_follow_up: 'Send Follow-up',
    add_memory: 'Save to Memory',
    update_contact: 'Update Contact',
  };

  return (
    <div>
      <button
        onClick={onToggle}
        className="flex items-center gap-1.5 text-xs font-medium text-purple-700 hover:text-purple-900"
      >
        {isExpanded ? (
          <ChevronUp className="w-3 h-3" />
        ) : (
          <ChevronDown className="w-3 h-3" />
        )}
        Suggested Actions ({actions.length})
      </button>

      {isExpanded && (
        <div className="mt-2 space-y-2">
          {actions.map((action) => (
            <div
              key={action.id}
              className="bg-white/60 rounded-lg p-3 flex items-center justify-between gap-3"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900">
                  {actionTypeLabels[action.type] || action.type}
                </p>
                <p className="text-xs text-gray-500 truncate">{action.summary}</p>
                <p className="text-xs text-gray-400 mt-0.5">{action.explanation}</p>
              </div>
              <button
                onClick={() => onExecute(action)}
                disabled={pendingActionId !== null}
                className="flex-shrink-0 px-3 py-1.5 text-xs font-medium bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
              >
                {pendingActionId === action.id ? 'Working…' : 'Execute'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ThreadAISummary;
